import React, { useState, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Stack, Box, Text, HStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import map from 'lodash/map';
import round from 'lodash/round';
import { useSelector, useDispatch } from 'react-redux';

import { AreaChart } from 'app/components/Visualization/Charts';
import { formatNumberWithComma, StackedHeader } from 'components/DataDisplay';
import { HistoricalTimelineIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { CustomTooltip } from 'components/Visualization/Charts/CustomTooltip';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { ComplianceChartDetails } from 'containers/Compliance/Components/ComplianceChartDetails';
import { historyChartStyles } from 'containers/Compliance/Components/ComplianceDashboard/styles';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { selectCompliance } from 'containers/Compliance/selectors';
import { renderPercentChange } from 'containers/Dashboard/utils/utils';

import { customTheme } from '../../../../theme';
import { actions } from '../slice';

interface Props {
  ruleId?: string;
  policyDescription?: string;
  resourceType?: string;
  control?: string;
  subControl?: string;
  severity?: string;
}

export const History: React.FC<Props> = props => {
  const {
    ruleId,
    resourceType,
    control,
    subControl,
    policyDescription,
  } = props;
  const dispatch = useDispatch();
  const { selectedResource, isOpenHistory, onCloseHistory } = useCompliance();
  const { selectedBlueprint, environment } = useSelector(selectCompliance);
  const { actionHistory } = useSelector(selectCompliance);
  const [data, setData] = useState<any>();
  const [percentChange, setPercentChange] = useState(0);

  const { withAccessBoundary } = useAccessBoundary();

  useEffect(() => {
    const startDate = dayjs().subtract(90, 'days').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');

    if (!!selectedBlueprint && !isEmpty(selectedResource) && isOpenHistory) {
      dispatch(
        actions.getActionHistoricalTrend({
          q: {
            blueprintId: selectedBlueprint?.id,
            resourceId: selectedResource?.resource_id,
            control: control ?? selectedResource?.control ?? undefined,
            subControl:
              subControl ?? selectedResource?.sub_control ?? undefined,
            ruleId: ruleId ?? selectedResource?.rule_id,
            startDate,
            endDate,
            resourceType: (resourceType ??
              selectedResource?.type) as NativeResources,
            environmentId: environment?.id,
          },
        }),
      );
    }
  }, [
    dispatch,
    selectedBlueprint,
    selectedResource,
    withAccessBoundary,
    ruleId,
    resourceType,
    control,
    subControl,
    isOpenHistory,
    environment,
  ]);

  useEffect(() => {
    setData(
      map(actionHistory?.data, (o, key) => ({
        name: dayjs(key).format('DD-MMM'),
        total_performed: o?.total_performed,
        passed: o?.passed,
        'Checks Performed': formatNumberWithComma(o?.total_performed),
        'Passed Policy Checks': formatNumberWithComma(
          ((o?.passed / o?.total_performed) * 100).toFixed(0),
        ),
      })),
    );
  }, [actionHistory.data]);

  useEffect(() => {
    if (!isEmpty(data)) {
      const val =
        (data?.[data?.length - 1]?.passed ?? 0) - (data?.[0]?.passed ?? 0);
      if (val === 0) setPercentChange(0);
      else
        setPercentChange(
          !!data?.[0]?.passed ? round((val / data?.[0]?.passed) * 100, 1) : 100,
        );
    }
  }, [data]);

  const customTooltipContent = props => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const { payload: obj } = payload[0] ?? {};
      const payload0 = payload[0] ?? {};
      const payload1 = payload[1] ?? {};

      return (
        <>
          <CustomTooltip>
            <Stack fontSize={'sm'}>
              <Text>{obj.name}</Text>
              <Text color={payload0?.color}>
                Checks Performed:{' '}
                {formatNumberWithComma(obj?.['Checks Performed'])}
              </Text>
              <Text color={payload1?.color}>
                Passed Checks: {formatNumberWithComma(obj?.passed)} (
                {obj?.['Passed Checks']}%)
              </Text>
            </Stack>
          </CustomTooltip>
        </>
      );
    }

    return null;
  };

  return (
    <Modal
      isOpen={isOpenHistory}
      onClose={onCloseHistory}
      header={
        <StackedHeader
          upper={
            selectedResource?.description ??
            (!!control
              ? 'Control ' +
                control +
                (!!subControl ? ' / Sub-Control ' + subControl : '')
              : policyDescription)
          }
          lower="Last 90 days - Daily"
          icon={<HistoricalTimelineIcon />}
        />
      }
      styles={{
        modal: { size: '5xl' },
      }}
      body={
        <Stack spacing={4}>
          <ComplianceChartDetails
            resource={selectedResource}
            blueprint={selectedBlueprint}
            policyDescription={
              policyDescription ?? selectedResource?.description
            }
            severity={selectedResource?.severity}
            applicableEntities={
              !!control
                ? selectedResource?.check_stats?.total_entities
                : undefined
            }
          />
          <Stack
            h="320px"
            pt={6}
            px={4}
            pb={3}
            border="1px solid"
            borderColor="componentBorder"
            borderRadius={6}
          >
            <Box>
              <HStack
                justify="flex-end"
                w="full"
                color="#666"
                spacing={6}
                fontSize="sm"
              >
                <HStack align="center">
                  <Box w={5} borderTop="1px solid" borderColor="primary" />
                  <Box>% Passed Policy Checks</Box>
                </HStack>
                <HStack>
                  <Box>Change:</Box>
                  {renderPercentChange(
                    percentChange,
                    percentChange > 0
                      ? customTheme.colors.green['300']
                      : percentChange === 0
                      ? 'inherit'
                      : 'critical',
                  )}
                </HStack>
              </HStack>
            </Box>
            <Box flex={1} w="full" h="full">
              <AreaChart
                data={data}
                isLoading={actionHistory.isLoading}
                xField="name"
                yField={['Passed Policy Checks']}
                legend={false}
                colorMap={{
                  'Passed Policy Checks': customTheme.colors.primary,
                }}
                styles={historyChartStyles()}
                customTooltipContent={customTooltipContent}
              />
            </Box>
          </Stack>
        </Stack>
      }
    />
  );
};
